/**
 * Chat Stream Service
 *
 * Handles SSE streaming connection via fetch + ReadableStream.
 * NOT using EventSource because we need:
 *   - Authorization header (Bearer token)
 *   - POST-triggered streaming (EventSource only does GET)
 *   - AbortController integration
 *
 * Functions:
 *   - sendMessage: POST /chats/:id/messages (triggers stream on backend)
 *   - openStream: GET /chats/:id/stream (SSE connection via fetch)
 *   - abortStream: POST /chats/:id/abort (kill in-flight generation)
 */

import type {
  SSEEvent,
  SSEEventType,
  RawSSEFrame,
  SendMessageRequest,
  SendMessageResponse,
  AbortStreamResponse,
} from '@/types/stream.types';
import { SSEParser } from '@/utils/sseParser';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const HEARTBEAT_TIMEOUT_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MULTIPLIER = 3;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL || '/api';
}

function getAuthToken(): string | null {
  try {
    return localStorage.getItem('opus-ia-token');
  } catch {
    return null;
  }
}

function generateIdempotencyKey(): string {
  return `${Date.now().toString(36)}-${crypto.randomUUID()}`;
}

function buildAuthHeaders(lastEventId?: string): HeadersInit {
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    'Cache-Control': 'no-cache',
  };

  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (lastEventId) {
    headers['Last-Event-ID'] = lastEventId;
  }

  return headers;
}

function parseSSEFrame(frame: RawSSEFrame): SSEEvent | null {
  try {
    const data = JSON.parse(frame.data);
    const type = frame.event as SSEEventType;
    return { type, data, id: frame.id } as SSEEvent;
  } catch {
    // Malformed JSON in data field -- skip this event
    return null;
  }
}

// ---------------------------------------------------------------------------
// sendMessage
// ---------------------------------------------------------------------------

export async function sendMessage(
  chatId: string,
  request: SendMessageRequest,
  signal?: AbortSignal,
): Promise<SendMessageResponse> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Idempotency-Key': generateIdempotencyKey(),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(
    `${getBaseUrl()}/v1/chats/${chatId}/messages`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      credentials: 'include',
      signal,
    },
  );

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new StreamServiceError(
      response.status,
      typeof body.message === 'string' ? body.message : `Send failed (${response.status})`,
      typeof body.error_code === 'string' ? body.error_code : (typeof body.code === 'string' ? body.code : undefined),
      typeof body.details === 'object' && body.details !== null ? body.details as Record<string, unknown> : undefined,
    );
  }

  return response.json() as Promise<SendMessageResponse>;
}

// ---------------------------------------------------------------------------
// abortStream
// ---------------------------------------------------------------------------

export async function abortStream(chatId: string): Promise<AbortStreamResponse> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(
    `${getBaseUrl()}/v1/chats/${chatId}/abort`,
    {
      method: 'POST',
      headers,
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new StreamServiceError(
      response.status,
      typeof body.message === 'string' ? body.message : `Abort failed (${response.status})`,
      typeof body.code === 'string' ? body.code : undefined,
    );
  }

  return response.json() as Promise<AbortStreamResponse>;
}

// ---------------------------------------------------------------------------
// openStream (async generator yielding SSEEvent)
// ---------------------------------------------------------------------------

export interface OpenStreamOptions {
  chatId: string;
  signal: AbortSignal;
  lastEventId?: string;
  onReconnect?: (attempt: number) => void;
}

/**
 * Opens a fetch-based SSE connection and yields typed events.
 *
 * - Handles heartbeat timeout (30s without any event = error)
 * - Handles reconnection with exponential backoff (1s, 3s, 9s)
 * - Respects AbortSignal for cancellation
 * - Yields all event types including heartbeat (consumer decides what to skip)
 */
export async function* openStream(
  options: OpenStreamOptions,
): AsyncGenerator<SSEEvent, void, undefined> {
  const { chatId, signal, onReconnect } = options;
  let lastEventId = options.lastEventId;
  let reconnectAttempts = 0;

  while (reconnectAttempts <= MAX_RECONNECT_ATTEMPTS) {
    if (signal.aborted) return;

    try {
      const events = streamConnection(chatId, signal, lastEventId);

      for await (const event of events) {
        // Reset reconnect counter on successful event
        reconnectAttempts = 0;

        // Track last event ID for reconnection
        if (event.id) {
          lastEventId = event.id;
        }

        yield event;

        // Terminal events: stop the stream
        if (
          event.type === 'message_complete' ||
          event.type === 'abort_acknowledged' ||
          (event.type === 'error' && !event.data.retryable)
        ) {
          return;
        }
      }

      // Stream ended normally (reader done) without a terminal event.
      // This could be a network disconnect -- attempt reconnect.
      // But if the signal is aborted, just stop.
      if (signal.aborted) return;

    } catch (err: unknown) {
      if (signal.aborted) return;

      // If it's a non-retryable error, throw immediately
      if (err instanceof StreamServiceError && !isRetryableStatus(err.status)) {
        throw err;
      }

      // Network error or retryable HTTP error -- attempt reconnect
    }

    // Reconnect with exponential backoff
    reconnectAttempts++;
    if (reconnectAttempts > MAX_RECONNECT_ATTEMPTS) {
      throw new StreamServiceError(
        0,
        `Connection lost after ${MAX_RECONNECT_ATTEMPTS} reconnect attempts`,
        'E_RECONNECT_EXHAUSTED',
      );
    }

    onReconnect?.(reconnectAttempts);

    const delay = RECONNECT_BASE_MS * Math.pow(RECONNECT_MULTIPLIER, reconnectAttempts - 1);
    await sleep(delay, signal);
  }
}

// ---------------------------------------------------------------------------
// Internal: single stream connection (no reconnect logic)
// ---------------------------------------------------------------------------

async function* streamConnection(
  chatId: string,
  signal: AbortSignal,
  lastEventId?: string,
): AsyncGenerator<SSEEvent, void, undefined> {
  const response = await fetch(
    `${getBaseUrl()}/v1/chats/${chatId}/stream`,
    {
      method: 'GET',
      headers: buildAuthHeaders(lastEventId),
      credentials: 'include',
      signal,
    },
  );

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new StreamServiceError(
      response.status,
      typeof body.message === 'string' ? body.message : `Stream failed (${response.status})`,
      typeof body.code === 'string' ? body.code : undefined,
    );
  }

  if (!response.body) {
    throw new StreamServiceError(0, 'Response body is null', 'E_NO_BODY');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8', { fatal: false });
  const parser = new SSEParser();
  let heartbeatTimer: ReturnType<typeof setTimeout> | null = null;

  const resetHeartbeatTimer = (): Promise<never> => {
    if (heartbeatTimer) clearTimeout(heartbeatTimer);
    return new Promise<never>((_, reject) => {
      heartbeatTimer = setTimeout(() => {
        reject(new StreamServiceError(0, 'Heartbeat timeout (30s without events)', 'E_HEARTBEAT_TIMEOUT'));
      }, HEARTBEAT_TIMEOUT_MS);
    });
  };

  let heartbeatPromise = resetHeartbeatTimer();

  try {
    while (true) {
      if (signal.aborted) return;

      const readPromise = reader.read();

      // Race between read and heartbeat timeout
      const result = await Promise.race([
        readPromise.then((r) => ({ type: 'read' as const, ...r })),
        heartbeatPromise.then(
          () => ({ type: 'timeout' as const, done: true as const, value: undefined }),
          (err) => { throw err; },
        ),
      ]);

      if (result.done) break;

      // Reset heartbeat timer on any data received
      heartbeatPromise = resetHeartbeatTimer();

      const text = decoder.decode(result.value, { stream: true });
      const frames = parser.feed(text);

      for (const frame of frames) {
        const event = parseSSEFrame(frame);
        if (event) {
          yield event;
        }
      }
    }
  } finally {
    if (heartbeatTimer) clearTimeout(heartbeatTimer);
    // Suppress unhandled rejection from the dangling heartbeat promise
    heartbeatPromise.catch(() => { /* intentionally swallowed on cleanup */ });
    reader.releaseLock();
  }
}

// ---------------------------------------------------------------------------
// Error class
// ---------------------------------------------------------------------------

export class StreamServiceError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'StreamServiceError';
  }
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function isRetryableStatus(status: number): boolean {
  // 0 = network error, 502/503/504 = upstream issues
  return status === 0 || status === 502 || status === 503 || status === 504;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timer = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };

    signal.addEventListener('abort', onAbort, { once: true });
  });
}
